import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Trash2, Edit, Save, X, Upload, Image as ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Project {
  id: string;
  title: string;
  description: string;
  images: string[];
  video_url: string | null;
  video_urls: string[];
  instagram_urls: string[];
  tags: string[];
  order_index: number;
  client_name: string | null;
}

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const [instagramUrls, setInstagramUrls] = useState<string[]>([]);
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    tags: string;
    client_name: string;
  }>({
    title: "",
    description: "",
    tags: "",
    client_name: "",
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Error checking admin role:", error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
      if (!data) {
        toast({
          title: "Access Denied",
          description: "You need admin privileges to access this page.",
          variant: "destructive",
        });
        navigate("/");
      }
    };

    checkAdminRole();
  }, [user, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchProjects();
    }
  }, [isAdmin]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from("portfolio_projects")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) throw error;
      setProjects((data || []) as Project[]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load projects",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls: string[] = [];
    let failedCount = 0;

    try {
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          failedCount++;
          continue;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          failedCount++;
          continue;
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
        const filePath = fileName;

        const { error: uploadError } = await supabase.storage
          .from('portfolio-images')
          .upload(filePath, file);

        if (uploadError) {
          failedCount++;
          continue;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('portfolio-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setUploadedImages([...uploadedImages, ...uploadedUrls]);

      if (uploadedUrls.length > 0) {
        toast({
          title: "Success",
          description: `${uploadedUrls.length} image${uploadedUrls.length > 1 ? 's' : ''} uploaded${failedCount > 0 ? ` (${failedCount} failed)` : ''}`,
        });
      } else {
        toast({
          title: "Error",
          description: "No valid images were uploaded",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Error",
        description: "Failed to upload images",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (indexToRemove: number) => {
    setUploadedImages(uploadedImages.filter((_, idx) => idx !== indexToRemove));
  };

  const moveImage = (fromIndex: number, direction: 'up' | 'down') => {
    const newImages = [...uploadedImages];
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    
    if (toIndex < 0 || toIndex >= newImages.length) return;
    
    [newImages[fromIndex], newImages[toIndex]] = [newImages[toIndex], newImages[fromIndex]];
    setUploadedImages(newImages);
  };

  const addVideoUrl = () => {
    setVideoUrls([...videoUrls, ""]);
  };

  const updateVideoUrl = (index: number, value: string) => {
    const newUrls = [...videoUrls];
    newUrls[index] = value;
    setVideoUrls(newUrls);
  };

  const removeVideoUrl = (index: number) => {
    setVideoUrls(videoUrls.filter((_, idx) => idx !== index));
  };

  const moveVideoUrl = (fromIndex: number, direction: 'up' | 'down') => {
    const newUrls = [...videoUrls];
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    
    if (toIndex < 0 || toIndex >= newUrls.length) return;
    
    [newUrls[fromIndex], newUrls[toIndex]] = [newUrls[toIndex], newUrls[fromIndex]];
    setVideoUrls(newUrls);
  };

  const addInstagramUrl = () => {
    setInstagramUrls([...instagramUrls, ""]);
  };

  const updateInstagramUrl = (index: number, value: string) => {
    const newUrls = [...instagramUrls];
    newUrls[index] = value;
    setInstagramUrls(newUrls);
  };

  const removeInstagramUrl = (index: number) => {
    setInstagramUrls(instagramUrls.filter((_, idx) => idx !== index));
  };

  const moveInstagramUrl = (fromIndex: number, direction: 'up' | 'down') => {
    const newUrls = [...instagramUrls];
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    
    if (toIndex < 0 || toIndex >= newUrls.length) return;
    
    [newUrls[fromIndex], newUrls[toIndex]] = [newUrls[toIndex], newUrls[fromIndex]];
    setInstagramUrls(newUrls);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const tagsArray = formData.tags
      .split(",")
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    const filteredVideoUrls = videoUrls.filter(url => url.trim().length > 0);
    const filteredInstagramUrls = instagramUrls.filter(url => url.trim().length > 0);

    const projectData = {
      title: formData.title,
      description: formData.description,
      images: uploadedImages,
      video_url: filteredVideoUrls.length > 0 ? filteredVideoUrls[0] : null,
      video_urls: filteredVideoUrls,
      instagram_urls: filteredInstagramUrls,
      tags: tagsArray,
      order_index: projects.length,
      client_name: formData.client_name || null,
    };

    try {
      if (editingId) {
        const { error } = await supabase
          .from("portfolio_projects")
          .update(projectData)
          .eq("id", editingId);

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Project updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("portfolio_projects")
          .insert([projectData]);

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Project added successfully",
        });
      }

      resetForm();
      fetchProjects();
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Error",
        description: "Failed to save project",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (project: Project) => {
    setEditingId(project.id);
    setUploadedImages(project.images || []);
    // Use video_urls if available, otherwise fall back to video_url
    const urls = project.video_urls?.length > 0 
      ? project.video_urls 
      : (project.video_url ? [project.video_url] : []);
    setVideoUrls(urls);
    setInstagramUrls(project.instagram_urls || []);
    setFormData({
      title: project.title,
      description: project.description,
      tags: project.tags.join(", "),
      client_name: project.client_name || "",
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this project?")) return;

    try {
      const { error } = await supabase
        .from("portfolio_projects")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project deleted successfully",
      });
      fetchProjects();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setUploadedImages([]);
    setVideoUrls([]);
    setInstagramUrls([]);
    setFormData({
      title: "",
      description: "",
      tags: "",
      client_name: "",
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-6 overflow-y-auto">
      <div className="container mx-auto max-w-6xl pb-12">
        <button
          onClick={() => navigate("/")}
          className="mb-6 group flex items-center gap-2 text-foreground/60 hover:text-foreground transition-all duration-300"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
          Portfolio Admin Panel
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {editingId ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {editingId ? "Edit Project" : "Add New Project"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_name">Client Name</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="e.g. Nike, Adidas, Client ABC"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used for filtering projects by client in portfolio
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Images (Multiple)</Label>
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/*"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <div className="text-sm">
                        <span className="text-primary font-medium">Click to upload</span>
                        <span className="text-muted-foreground"> or drag and drop</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        PNG, JPG, WEBP up to 5MB each (multiple files allowed)
                      </p>
                    </label>
                  </div>
                  {uploading && (
                    <p className="text-sm text-muted-foreground text-center">Uploading...</p>
                  )}
                  {uploadedImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {uploadedImages.map((url, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={url} 
                            alt={`Upload ${idx + 1}`}
                            className="w-full h-24 object-cover rounded border border-border" 
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-1 right-1 bg-destructive text-destructive-foreground p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                            {idx + 1}
                          </span>
                          <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {idx > 0 && (
                              <button
                                type="button"
                                onClick={() => moveImage(idx, 'up')}
                                className="bg-primary text-primary-foreground p-1 rounded text-xs"
                              >
                                ↑
                              </button>
                            )}
                            {idx < uploadedImages.length - 1 && (
                              <button
                                type="button"
                                onClick={() => moveImage(idx, 'down')}
                                className="bg-primary text-primary-foreground p-1 rounded text-xs"
                              >
                                ↓
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>YouTube Video URLs (Multiple)</Label>
                  <div className="space-y-2">
                    {videoUrls.map((url, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          type="url"
                          value={url}
                          onChange={(e) => updateVideoUrl(idx, e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
                          className="flex-1"
                        />
                        <div className="flex gap-1">
                          {idx > 0 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => moveVideoUrl(idx, 'up')}
                              className="h-8 w-8"
                            >
                              ↑
                            </Button>
                          )}
                          {idx < videoUrls.length - 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => moveVideoUrl(idx, 'down')}
                              className="h-8 w-8"
                            >
                              ↓
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            onClick={() => removeVideoUrl(idx)}
                            className="h-8 w-8"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addVideoUrl}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Video URL
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Supports youtube.com/watch?v= or youtube.com/embed/ formats
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Instagram Video URLs (Multiple)</Label>
                  <div className="space-y-2">
                    {instagramUrls.map((url, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          type="url"
                          value={url}
                          onChange={(e) => updateInstagramUrl(idx, e.target.value)}
                          placeholder="https://www.instagram.com/reel/ABC123/ or /p/ABC123/"
                          className="flex-1"
                        />
                        <div className="flex gap-1">
                          {idx > 0 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => moveInstagramUrl(idx, 'up')}
                              className="h-8 w-8"
                            >
                              ↑
                            </Button>
                          )}
                          {idx < instagramUrls.length - 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => moveInstagramUrl(idx, 'down')}
                              className="h-8 w-8"
                            >
                              ↓
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            onClick={() => removeInstagramUrl(idx)}
                            className="h-8 w-8"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addInstagramUrl}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Instagram URL
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Supports instagram.com/reel/, /p/, or /tv/ formats
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="Facebook, Instagram, ROI"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? "Update Project" : "Add Project"}
                  </Button>
                  {editingId && (
                    <Button type="button" variant="outline" onClick={resetForm}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Projects List */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Existing Projects ({projects.length})</h2>
            <div className="space-y-3 max-h-[800px] overflow-y-auto pr-2">
              {projects.map((project) => (
                <Card key={project.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{project.title}</h3>
                        {project.client_name && (
                          <Badge variant="secondary" className="mt-1">
                            {project.client_name}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => handleEdit(project)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="destructive"
                          onClick={() => handleDelete(project.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{project.description}</p>
                    {(project.video_urls?.length > 0 || project.video_url) && (
                      <p className="text-xs text-primary mb-2">
                        📹 {project.video_urls?.length || 1} YouTube video{(project.video_urls?.length || 1) > 1 ? 's' : ''}
                      </p>
                    )}
                    {project.instagram_urls && project.instagram_urls.length > 0 && (
                      <p className="text-xs text-pink-500 mb-2">
                        📸 {project.instagram_urls.length} Instagram video{project.instagram_urls.length > 1 ? 's' : ''}
                      </p>
                    )}
                    {project.images && project.images.length > 0 && (
                      <p className="text-xs text-muted-foreground mb-2">🖼️ {project.images.length} image{project.images.length > 1 ? 's' : ''}</p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {project.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
